package br.com.vvrefeicoes.portal.feature.users

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.util.file.DocumentDropZone
import br.com.vvrefeicoes.portal.core.util.file.isDocumentUploadSupported
import br.com.vvrefeicoes.portal.domain.model.UserAccount
import br.com.vvrefeicoes.portal.domain.model.UserDocument
import br.com.vvrefeicoes.portal.domain.model.availableUserDocumentTypes
import com.composables.icons.lucide.Download
import com.composables.icons.lucide.Eye
import com.composables.icons.lucide.FileText
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Plus
import com.composables.icons.lucide.Trash2
import com.composables.icons.lucide.Upload

@Composable
fun UserDocumentsDialog(
    user: UserAccount,
    onDismiss: () -> Unit,
) {
    var selectedType by remember { mutableStateOf(availableUserDocumentTypes.first()) }
    var documentToDelete by remember { mutableStateOf<UserDocument?>(null) }
    val controller = rememberUserDocumentsController(user.id, selectedType)
    var query by remember { mutableStateOf("") }
    val documents = controller.documents.filter { it.source == selectedType.source }

    LaunchedEffect(user.id) { controller.init() }

    AppModalCard(
        title = "Documentos de ${user.fullName}",
        onDismiss = onDismiss,
    ) {
        Text(
            text = "${documents.size} documento(s)",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = "Tipo de documento",
            style = MaterialTheme.typography.labelLarge,
        )
        SearchableSelect(
            leadingIcon = Lucide.FileText,
            options = availableUserDocumentTypes.map { it.label },
            selected = selectedType.label,
            onSelect = { label ->
                selectedType = availableUserDocumentTypes.first { it.label == label }
                query = ""
            },
            modifier = Modifier.fillMaxWidth(),
            placeholder = "Selecione o tipo",
        )
        controller.errorMessage?.let {
            Text(text = it, color = MaterialTheme.colorScheme.error)
        }
        controller.successMessage?.let {
            Text(text = it, color = MaterialTheme.colorScheme.primary)
        }
        AppTextField(
            value = query,
            onValueChange = { query = it },
            placeholder = "Filtrar na página...",
            modifier = Modifier.fillMaxWidth(),
        )
        UploadQueue(controller)
        if (controller.loading) {
            Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        } else {
            val filtered = documents.filter { it.originalFileName.contains(query, ignoreCase = true) }
            if (filtered.isEmpty()) {
                if (controller.pendingFiles.isEmpty()) EmptyState(message = "Nenhum documento.")
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    filtered.forEach { document ->
                        DocumentRow(document, controller) { documentToDelete = it }
                    }
                }
            }
        }
        AppButton(
            text = "Fechar",
            onClick = onDismiss,
            variant = PortalButtonVariant.Outlined,
            modifier = Modifier.align(Alignment.End),
        )
    }

    documentToDelete?.let { document ->
        AppModalCard(
            title = "Excluir documento",
            onDismiss = { documentToDelete = null },
        ) {
            Text(
                text = "Deseja realmente excluir o documento \"${document.originalFileName}\"?",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
            ) {
                AppButton(
                    text = "Cancelar",
                    onClick = { documentToDelete = null },
                    variant = PortalButtonVariant.Text,
                )
                AppButton(
                    text = "Excluir",
                    onClick = {
                        controller.delete(document)
                        documentToDelete = null
                    },
                    variant = PortalButtonVariant.Filled,
                )
            }
        }
    }
}

@Composable
private fun UploadQueue(controller: UserDocumentsController) {
    DocumentDropZone(onFiles = { files -> files.forEach(controller::addFile) }) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .border(
                        BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                        RoundedCornerShape(12.dp),
                    ).padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Icon(Lucide.Upload, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
                Column(Modifier.weight(1f)) {
                    Text("Arraste e solte arquivos aqui", style = MaterialTheme.typography.bodyMedium)
                    Text("Os arquivos serão adicionados à fila. Clique em Salvar para enviar.", style = MaterialTheme.typography.bodySmall)
                }
                AppButton(
                    text = "Inserir arquivos",
                    onClick = { if (isDocumentUploadSupported()) controllerPick(controller) },
                    variant = PortalButtonVariant.Outlined,
                    leadingIcon = Lucide.Plus,
                )
            }
            controller.pendingFiles.forEach { file ->
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(file.name, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                    IconButton(onClick = { controller.removePending(file) }) {
                        Icon(Lucide.Trash2, contentDescription = "Remover")
                    }
                }
            }
            if (controller.pendingFiles.isNotEmpty()) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    AppButton(
                        text = "Salvar",
                        onClick = controller::save,
                        enabled = !controller.saving,
                        variant = PortalButtonVariant.Filled,
                        leadingIcon = Lucide.Upload,
                    )
                    AppButton(
                        text = "Limpar fila",
                        onClick = controller::clearPending,
                        enabled = !controller.saving,
                        variant = PortalButtonVariant.Text,
                    )
                }
            }
        }
    }
}

private fun controllerPick(controller: UserDocumentsController) {
    // The platform picker is suspendable; the dialog invokes it through the shared scope.
    controller.pickNextFile()
}

@Composable
private fun DocumentRow(
    document: UserDocument,
    controller: UserDocumentsController,
    onDelete: (UserDocument) -> Unit,
) {
    Surface(
        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.25f),
        shape = RoundedCornerShape(8.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Icon(Lucide.FileText, contentDescription = null, tint = MaterialTheme.colorScheme.primary)
            Column(Modifier.weight(1f)) {
                Text(document.originalFileName, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text("${document.source} · ${formatBytes(document.sizeBytes)}", style = MaterialTheme.typography.bodySmall)
            }
            IconButton(onClick = { controller.open(document) }) {
                Icon(Lucide.Eye, contentDescription = "Abrir")
            }
            IconButton(onClick = { controller.download(document) }) {
                Icon(Lucide.Download, contentDescription = "Baixar")
            }
            IconButton(onClick = { onDelete(document) }) {
                Icon(Lucide.Trash2, contentDescription = "Excluir")
            }
        }
    }
}

private fun formatBytes(bytes: Long): String =
    when {
        bytes < 1024 -> "$bytes B"
        bytes < 1024 * 1024 -> "${bytes / 1024} KB"
        else -> "${bytes / (1024 * 1024)} MB"
    }
